import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ImageUploader from '@/components/feature/ImageUploader';
import RichMdEditor from '@/pages/admin/articles/components/RichMdEditor';

interface Category {
  id: number;
  name_zh: string;
}

interface ArticleForm {
  title_zh: string;
  title_en: string;
  title_ru: string;
  summary_zh: string;
  summary_en: string;
  summary_ru: string;
  content_zh: string;
  content_en: string;
  content_ru: string;
  cover_img: string;
  tags_str: string;
  category_id: string;
  related_ids_str: string;
  status: string;
  featured: boolean;
  sort_order: number;
  scheduled_at: string;
  published_at: string;
}

const INIT_FORM: ArticleForm = {
  title_zh: '', title_en: '', title_ru: '',
  summary_zh: '', summary_en: '', summary_ru: '',
  content_zh: '', content_en: '', content_ru: '',
  cover_img: '', tags_str: '',
  category_id: '', related_ids_str: '', status: 'draft',
  featured: false, sort_order: 5,
  scheduled_at: '', published_at: '',
};

function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type LangTab = 'zh' | 'en' | 'ru';

export default function ArticleEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState<ArticleForm>(INIT_FORM);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allArticles, setAllArticles] = useState<{ id: number; title_zh: string }[]>([]);
  const [lang, setLang] = useState<LangTab>('zh');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [relatedSearch, setRelatedSearch] = useState('');

  const fetchInit = useCallback(async () => {
    const [catRes, artListRes] = await Promise.all([
      supabase.from('article_categories').select('id, name_zh').order('sort_order'),
      supabase.from('articles').select('id, title_zh').eq('status', 'published').order('created_at', { ascending: false }),
    ]);
    const cats = catRes.data ?? [];
    setCategories(cats);
    setAllArticles(artListRes.data ?? []);

    if (!isNew && id) {
      const { data } = await supabase.from('articles').select('*').eq('id', id).maybeSingle();
      if (data) {
        setForm({
          title_zh: data.title_zh || '',
          title_en: data.title_en || '',
          title_ru: data.title_ru || '',
          summary_zh: data.summary_zh || '',
          summary_en: data.summary_en || '',
          summary_ru: data.summary_ru || '',
          content_zh: data.content_zh || '',
          content_en: data.content_en || '',
          content_ru: data.content_ru || '',
          cover_img: data.cover_img || '',
          tags_str: (data.tags || []).join(', '),
          category_id: data.category_id ? String(data.category_id) : '',
          related_ids_str: (data.related_ids || []).join(', '),
          status: data.status || 'draft',
          featured: data.featured ?? false,
          sort_order: data.sort_order ?? 5,
          scheduled_at: data.scheduled_at ? toLocalInput(data.scheduled_at) : '',
          published_at: data.published_at || '',
        });
      }
    } else if (isNew && cats.length > 0) {
      setForm((prev) => ({ ...prev, category_id: String(cats[0].id) }));
    }
    setLoading(false);
  }, [id, isNew]);

  useEffect(() => { fetchInit(); }, [fetchInit]);

  const set = (key: keyof ArticleForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const isEditingPublished = !isNew && form.status === 'published';

  const handleSave = async (mode: 'draft' | 'publish') => {
    if (!form.title_zh) { setSaveMsg('请先填写中文标题'); return; }
    setSaving(true);
    setSaveMsg('');

    const now = new Date().toISOString();
    let status: string;
    let publishedAt: string | null;
    let scheduledAt: string | null;

    if (mode === 'draft') {
      status = 'draft';
      publishedAt = null;
      scheduledAt = null;
    } else {
      status = 'published';
      if (form.scheduled_at) {
        scheduledAt = new Date(form.scheduled_at).toISOString();
        publishedAt = scheduledAt;
      } else if (form.published_at) {
        scheduledAt = null;
        publishedAt = form.published_at;
      } else {
        scheduledAt = null;
        publishedAt = now;
      }
    }

    const payload = {
      title_zh: form.title_zh,
      title_en: form.title_en || null,
      title_ru: form.title_ru || null,
      summary_zh: form.summary_zh || null,
      summary_en: form.summary_en || null,
      summary_ru: form.summary_ru || null,
      content_zh: form.content_zh || null,
      content_en: form.content_en || null,
      content_ru: form.content_ru || null,
      cover_img: form.cover_img || null,
      tags: form.tags_str ? form.tags_str.split(',').map((t) => t.trim()).filter(Boolean) : [],
      category_id: form.category_id ? parseInt(form.category_id) : null,
      related_ids: form.related_ids_str
        ? form.related_ids_str.split(',').map((s) => parseInt(s.trim())).filter(Boolean)
        : [],
      status,
      featured: form.featured,
      sort_order: form.featured ? form.sort_order : 5,
      published_at: publishedAt,
      scheduled_at: scheduledAt,
      updated_at: now,
    };

    try {
      if (isNew) {
        const { data, error } = await supabase
          .from('articles')
          .insert({ ...payload, created_at: now })
          .select('id')
          .maybeSingle();
        if (!error && data) {
          setSaved(true);
          setSaveMsg(mode === 'publish' ? '文章已发布！即将跳转...' : '已保存为草稿，即将跳转...');
          setTimeout(() => navigate('/admin/articles'), 1200);
        } else if (error) {
          setSaveMsg(`保存失败：${error.message}`);
        } else {
          setSaveMsg('保存失败：未返回数据，请重试');
        }
      } else {
        const { error } = await supabase.from('articles').update(payload).eq('id', id);
        if (!error) {
          setSaved(true);
          setSaveMsg(
            mode === 'publish'
              ? (isEditingPublished ? '✓ 修改已保存' : '✓ 文章已发布')
              : '✓ 草稿已保存'
          );
          setTimeout(() => { setSaved(false); setSaveMsg(''); }, 2500);
        } else {
          setSaveMsg(`保存失败：${error.message}`);
        }
      }
    } catch (err: any) {
      setSaveMsg(`保存失败：${err?.message || '网络异常，请检查网络后重试'}`);
    }
    setSaving(false);
  };

  const currentContent = form[`content_${lang}` as keyof ArticleForm] as string;
  const langLabel: Record<LangTab, string> = { zh: '中文', en: 'English', ru: 'Русский' };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <i className="ri-loader-4-line text-3xl animate-spin block mb-2"></i>加载中...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed header */}
      <div className="flex items-center gap-4 px-6 py-4 bg-white border-b border-gray-100 flex-wrap sticky top-0 z-20">
        <Link
          to="/admin/articles"
          className="flex items-center gap-1.5 text-gray-400 hover:text-[#1a3a5c] text-sm cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>返回列表
        </Link>
        <h1 className="text-base font-bold text-[#1a3a5c]">{isNew ? '发布新文章' : '编辑文章'}</h1>

        <span className="hidden md:flex items-center gap-1.5 bg-[#f8f5f0] text-[#b8944f] text-xs font-medium px-3 py-1.5 rounded-lg ml-2 whitespace-nowrap">
          <i className="ri-image-line"></i>
          富文本编辑器 · 支持图片上传
        </span>

        <div className="ml-auto flex items-center gap-3">
          {saveMsg && (
            <span className={`text-xs ${saved ? 'text-green-600' : 'text-red-500'}`}>{saveMsg}</span>
          )}
          {!isEditingPublished && (
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="border border-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm cursor-pointer hover:border-[#c9a96e] transition-colors whitespace-nowrap disabled:opacity-60"
            >
              存草稿
            </button>
          )}
          <button
            onClick={() => handleSave('publish')}
            disabled={saving}
            className="bg-[#c9a96e] hover:bg-[#b8944f] text-white px-5 py-2 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 font-semibold"
          >
            {saving ? '保存中...' : isEditingPublished ? '保存修改' : '发布文章'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title & summary */}
          <div className="bg-white rounded-2xl p-5">
            <div className="flex gap-2 mb-4">
              {(['zh', 'en', 'ru'] as LangTab[]).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                    lang === l ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {langLabel[l]}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-400 self-center">当前编辑语言：{langLabel[lang]}</span>
            </div>

            <input
              type="text"
              value={form[`title_${lang}` as keyof ArticleForm] as string}
              onChange={set(`title_${lang}` as keyof ArticleForm)}
              placeholder={`${langLabel[lang]}标题${lang === 'zh' ? '（必填）' : '（选填）'}`}
              className="w-full border-0 border-b border-gray-200 py-2 text-xl font-bold text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] mb-4 bg-transparent"
            />

            <textarea
              value={form[`summary_${lang}` as keyof ArticleForm] as string}
              onChange={set(`summary_${lang}` as keyof ArticleForm)}
              rows={2}
              placeholder={`${langLabel[lang]}摘要（100字左右，显示在文章列表卡片）`}
              className="w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm text-gray-600 focus:outline-none focus:border-[#c9a96e] resize-none bg-[#f9f8f6]"
            />
          </div>

          {/* Rich Markdown Editor */}
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              {(['zh', 'en', 'ru'] as LangTab[]).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                    lang === l ? 'bg-[#1a3a5c] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {langLabel[l]}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-400">{currentContent.length} 字</span>
            </div>

            <RichMdEditor
              key={`rich-md-${lang}`}
              value={currentContent}
              onChange={(val) => {
                const key = `content_${lang}` as keyof ArticleForm;
                setForm((p) => ({ ...p, [key]: val }));
              }}
              height={540}
              lang={lang}
              placeholder={`在此输入${langLabel[lang]}正文内容，支持 Markdown 格式。点击工具栏图片图标可上传图片插入正文。`}
            />
          </div>
        </div>

        {/* Right sidebar */}
        <div className="w-72 shrink-0 overflow-y-auto border-l border-gray-100 bg-[#fafaf9] p-5 space-y-4">
          {/* Publish settings */}
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-[#1a3a5c] text-sm mb-3">发布设置</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">文章分类</label>
                <select
                  value={form.category_id}
                  onChange={set('category_id')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white"
                >
                  <option value="">请选择分类</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name_zh}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">定时发布（可选）</label>
                <input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={set('scheduled_at')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
                <p className="text-[11px] text-gray-400 mt-1">设置后文章将在指定时间自动上线；留空则立即发布</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">标签（逗号分隔）</label>
                <input
                  type="text"
                  value={form.tags_str}
                  onChange={set('tags_str')}
                  placeholder="IVF, 试管婴儿, 代孕"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div className="pt-2 border-t border-gray-100 space-y-2">
                <button
                  onClick={() =>
                    setForm((p) => ({ ...p, featured: !p.featured, sort_order: p.featured ? 5 : p.sort_order }))
                  }
                  className={`flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors ${
                    form.featured ? 'text-[#b8944f]' : 'text-gray-400'
                  }`}
                >
                  <i className={`${form.featured ? 'ri-star-fill' : 'ri-star-line'} text-base`}></i>
                  {form.featured ? '已设为首页推荐' : '设为首页推荐'}
                </button>
                {form.featured && (
                  <div className="flex items-center gap-2">
                    <select
                      value={form.sort_order}
                      onChange={(e) => setForm((p) => ({ ...p, sort_order: parseInt(e.target.value) }))}
                      className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#c9a96e] cursor-pointer bg-white"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <option key={n} value={n}>
                          {n} — {n <= 3 ? '最优先' : n <= 6 ? '中等' : '较低'}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-400">数字越小越靠前</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cover image */}
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-[#1a3a5c] text-sm mb-3">封面图片</h3>
            <ImageUploader
              currentUrl={form.cover_img}
              onChange={(url) => setForm((p) => ({ ...p, cover_img: url }))}
              category="knowledge"
              placeholder="点击上传封面或粘贴 URL（JPG/PNG/WebP，最大2MB）"
              previewHeight="h-36"
              maxWidth={400}
            />
            <p className="text-xs text-gray-400 mt-1.5">支持从电脑上传图片或粘贴外部 URL</p>
          </div>

          {/* Related articles */}
          <div className="bg-white rounded-2xl p-4">
            <h3 className="font-semibold text-[#1a3a5c] text-sm mb-1">关联推荐</h3>
            <p className="text-xs text-gray-400 mb-3">勾选最多3篇；不足3篇时系统按标签自动补齐</p>
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 bg-[#f8f5f0]">
              <i className="ri-search-line text-gray-400 text-sm"></i>
              <input
                type="text"
                value={relatedSearch}
                onChange={(e) => setRelatedSearch(e.target.value)}
                placeholder="按标题搜索文章..."
                className="bg-transparent text-xs text-gray-600 outline-none flex-1"
              />
            </div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {allArticles
                .filter((a) => !id || String(a.id) !== id)
                .filter((a) => !relatedSearch || (a.title_zh || '').toLowerCase().includes(relatedSearch.toLowerCase()))
                .map((a) => {
                  const arr = form.related_ids_str
                    ? form.related_ids_str.split(',').map((s) => s.trim()).filter(Boolean)
                    : [];
                  const selected = arr.includes(String(a.id));
                  const toggle = () => {
                    const updated = selected
                      ? arr.filter((x) => x !== String(a.id))
                      : [...arr, String(a.id)].slice(0, 3);
                    setForm((prev) => ({ ...prev, related_ids_str: updated.join(', ') }));
                  };
                  return (
                    <label
                      key={a.id}
                      className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                        selected ? 'bg-[#f8f5f0]' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={toggle}
                        className="accent-[#c9a96e] mt-0.5 shrink-0"
                      />
                      <span className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{a.title_zh}</span>
                    </label>
                  );
                })}
              {allArticles
                .filter((a) => !id || String(a.id) !== id)
                .filter((a) => !relatedSearch || (a.title_zh || '').toLowerCase().includes(relatedSearch.toLowerCase()))
                .length === 0 && (
                <p className="text-xs text-gray-400 text-center py-3">{relatedSearch ? '没有匹配的文章' : '发布更多文章后可关联'}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}